import { type RefObject, useEffect } from 'react';

/**
 * Give up a transient open state — an armed confirm, an open menu, a popover —
 * when the user's attention moves elsewhere: a press outside `containedBy`, or
 * Escape from anywhere.
 *
 * Does nothing while `open` is false, so the document listeners exist only for
 * as long as there is something to close. Pass a stable `dismiss` (`useCallback`
 * or a setter) — it is a dependency of the effect that binds them.
 */
export const useDismissOnOutsideInteraction = (
  containedBy: RefObject<HTMLElement | null>,
  open: boolean,
  dismiss: () => void,
) => {
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containedBy.current?.contains(event.target as Node)) dismiss();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [containedBy, open, dismiss]);
};
